import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  isProcessing,
  useAskMeeting,
  useDeleteMeeting,
  useMeeting,
  useRenameMeeting,
  useRenameParticipant,
  useSummariseMeeting,
} from "../../hooks/useMeetings";
import { useMeetingNotesTemplates } from "../../hooks/useMeetingNotesTemplates";
import { colors, fonts } from "../../style";
import {
  captureFor,
  formatMeetingDate,
  formatSpokenDuration,
  meetingAsText,
} from "../../utils/meetingFormat";
import {
  BackLink,
  Banner,
  Btn,
  Chip,
  ConfirmDialog,
  ErrorState,
  errorMessage,
  Icon,
  IconButton,
  Spinner,
  Tabs,
  TextInput,
  ui,
} from "../ui";
import { SummariseTemplatePicker } from "../sidekick/SummariseTemplatePicker";
import { EmailNotesPanel } from "./EmailNotesPanel";
import { AskTab } from "./MeetingAsk";
import {
  BookmarkList,
  EmailedCard,
  SummaryTab,
  UserNotesCard,
} from "./MeetingSummary";
import { TranscriptTab } from "./MeetingTranscript";
import { TalkShareCard } from "./TalkShareCard";

export const DETAIL_TABS = [
  { value: "summary", label: "Summary" },
  { value: "transcript", label: "Transcript" },
  { value: "ask", label: "Ask Sidekick" },
];

// `panel` sits beside the meetings list; `page` is the full page. Callers key
// it by sqid so switching meetings starts from fresh state.
export function MeetingView({ sqid, variant = "page" }) {
  const navigate = useNavigate();
  const {
    data: meeting,
    error,
    isLoading,
    isPlaceholderData,
    refetch,
  } = useMeeting(sqid);
  const rename = useRenameMeeting(sqid);
  const summarise = useSummariseMeeting(sqid);
  const renameSpeaker = useRenameParticipant(sqid);
  const ask = useAskMeeting(sqid);
  const remove = useDeleteMeeting();
  const { templates } = useMeetingNotesTemplates();
  const [pickedTab, setTab] = useState(null);
  // An object so clicking the same bookmark again scrolls again
  const [focusAt, setFocusAt] = useState(null);
  const [picking, setPicking] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const panel = variant === "panel";

  const close = () => navigate("/meetings");

  if (!meeting) {
    return (
      <div
        style={{
          padding: "40px 28px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          alignItems: "center",
          fontFamily: fonts.system,
        }}
      >
        {panel && (
          <Btn
            variant="ghost"
            icon="close"
            aria-label="Close"
            onClick={close}
            style={{ alignSelf: "flex-end" }}
          />
        )}
        {isLoading ? (
          <Spinner size={22} />
        ) : (
          <>
            <ErrorState
              card={false}
              onRetry={error?.response?.status !== 404 && (() => refetch())}
              retryLabel="Try again"
            >
              {error?.response?.status === 404
                ? "This meeting doesn't exist or was deleted."
                : "Couldn't load this meeting."}
            </ErrorState>
            {!panel && (
              <Link to="/meetings" style={{ color: colors.teal }}>
                Back to meetings
              </Link>
            )}
          </>
        )}
      </div>
    );
  }

  const status = meeting.status.value;
  const processing = isProcessing(meeting);
  const failed = status === "FAILED" || status === "DID_NOT_TRANSCRIBE";
  const noSpeech = status === "PROCESSED" && !meeting.has_transcription;
  const canSummarise = status === "PROCESSED" && meeting.has_transcription;
  const hasSummary = status !== "SUMMARISING" && !!meeting.summary;
  // The list row standing in while loading has no meeting_notes to copy
  const canCopy = hasSummary && !isPlaceholderData;
  const conversation = meeting.transcription?.conversation ?? [];
  const timed = conversation.some((b) => b.start != null);
  const tab =
    pickedTab ?? (meeting.summary || processing ? "summary" : "transcript");
  const capture = captureFor(meeting.capture);

  // With no custom templates there's nothing to pick, so keep the meeting's own
  const startSummarise = () =>
    templates.length > 0 ? setPicking(true) : runSummarise(undefined);

  const runSummarise = (templateId) => {
    setPicking(false);
    setTab("summary");
    summarise.mutate(templateId, {
      onError: (e) =>
        toast(errorMessage(e, "Couldn't start the summary. Try again."), {
          type: "error",
        }),
    });
  };

  // navigator.clipboard is missing outside secure contexts
  const copy = () =>
    (
      navigator.clipboard?.writeText(meetingAsText(meeting)) ?? Promise.reject()
    ).then(
      () => toast("Notes copied", { type: "success" }),
      () => toast("Couldn't copy the notes", { type: "error" }),
    );

  const jumpTo = (at) => {
    setTab("transcript");
    setFocusAt({ at });
  };

  const meta = [
    formatMeetingDate(meeting.recorded_at),
    formatSpokenDuration(meeting.duration_seconds),
    meeting.attendees?.length
      ? `with ${meeting.attendees
          .slice(0, 3)
          .map((a) => a.name || a.email)
          .join(
            ", ",
          )}${meeting.attendees.length > 3 ? ` and ${meeting.attendees.length - 3} more` : ""}`
      : null,
    !panel && meeting.meeting_notes_template
      ? `${meeting.meeting_notes_template} template`
      : null,
  ].filter(Boolean);

  const header = (
    <DetailHeader
      chip={
        <Chip color={capture.color} icon={capture.icon}>
          {capture.label}
        </Chip>
      }
      title={
        <EditableTitle
          value={meeting.title ?? ""}
          size={panel ? 22 : 26}
          onSave={(v) =>
            rename.mutate(v, {
              onError: (e) => toast(errorMessage(e), { type: "error" }),
            })
          }
        />
      }
      meta={meta.join(" · ")}
      actions={
        <>
          {hasSummary && !panel && (
            <>
              {canCopy && (
                <Btn icon="copy" onClick={copy}>
                  Copy notes
                </Btn>
              )}
              <Btn icon="mail" onClick={() => setEmailing(true)}>
                Email notes
              </Btn>
            </>
          )}
          <Btn
            icon="trash"
            aria-label="Delete meeting"
            title="Delete meeting"
            onClick={() => setConfirmDelete(true)}
          />
          {panel && (
            <>
              <Btn
                as={Link}
                to={`/meetings/${meeting.sqid}`}
                icon="expand"
                aria-label="Open full page"
                title="Open full page"
              />
              <Btn
                icon="close"
                aria-label="Close"
                title="Close"
                onClick={close}
              />
            </>
          )}
        </>
      }
    />
  );

  const deviceNote = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "10px 12px",
        borderRadius: "12px",
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        fontSize: "13px",
      }}
    >
      <Icon name="laptop" size={16} color={colors.textMuted} />
      <span style={{ ...ui.muted, flex: 1 }}>
        The recording is kept on the device that recorded it. Open it in
        Sidekick there to listen.
      </span>
    </div>
  );

  const states = (
    <>
      {processing && <ProcessingCard meeting={meeting} />}
      {failed && (
        <FailedCard meeting={meeting} onDelete={() => setConfirmDelete(true)} />
      )}
      {canSummarise &&
        (meeting.error_message ||
          (!meeting.summary && !summarise.isLoading)) && (
          <NotSummarisedCard
            meeting={meeting}
            loading={summarise.isLoading}
            onSummarise={startSummarise}
            onDelete={() => setConfirmDelete(true)}
          />
        )}
      {noSpeech && (
        <Banner tone="warning" icon="mic" title="No speech detected">
          Sidekick couldn't make out any words, so there's no transcript or
          summary. Check the microphone and try recording again.
        </Banner>
      )}
    </>
  );

  const tabs = meeting.has_transcription && (
    <>
      <Tabs
        ariaLabel="Meeting"
        value={tab}
        items={DETAIL_TABS}
        onChange={(t) => {
          setTab(t);
          setFocusAt(null);
        }}
      />
      {tab === "summary" && (
        <SummaryTab
          meeting={meeting}
          loading={isPlaceholderData}
          canResummarise={canSummarise}
          onResummarise={startSummarise}
        />
      )}
      {tab === "transcript" && (
        <TranscriptTab
          meeting={meeting}
          loading={isPlaceholderData}
          focusAt={focusAt}
          rename={renameSpeaker}
        />
      )}
      {tab === "ask" && <AskTab ask={ask} />}
    </>
  );

  const sideCards = (
    <>
      <TalkShareCard
        meeting={meeting}
        rename={renameSpeaker}
        onShowTranscript={
          tab === "transcript"
            ? undefined
            : () => {
                setTab("transcript");
                setFocusAt(null);
              }
        }
      />
      <UserNotesCard
        meeting={meeting}
        onResummarise={canSummarise ? startSummarise : undefined}
      />
      <EmailedCard meeting={meeting} />
    </>
  );

  return (
    <div
      style={{
        fontFamily: fonts.system,
        color: colors.textStrong,
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        padding: panel ? "24px" : "24px 32px 40px",
      }}
    >
      {!panel && <BackLink to="/meetings">Meetings</BackLink>}
      {header}
      {states}
      {deviceNote}

      {panel ? (
        <>
          <BookmarkList
            meeting={meeting}
            timed={timed}
            onJump={jumpTo}
            variant="chips"
          />
          {tabs}
          {sideCards}
          {hasSummary && (
            <div
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                marginTop: "4px",
              }}
            >
              {canCopy && (
                <Btn icon="copy" onClick={copy}>
                  Copy notes
                </Btn>
              )}
              <Btn icon="mail" onClick={() => setEmailing(true)}>
                Email notes
              </Btn>
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            display: "flex",
            gap: "20px",
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              flex: "999 1 460px",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}
          >
            {tabs}
          </div>
          <div
            style={{
              flex: "1 1 280px",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}
          >
            <BookmarkList meeting={meeting} timed={timed} onJump={jumpTo} />
            {sideCards}
          </div>
        </div>
      )}

      {picking && (
        <SummariseTemplatePicker
          open
          subject="meeting"
          onClose={() => setPicking(false)}
          onConfirm={runSummarise}
        />
      )}

      <EmailNotesPanel
        meeting={meeting}
        open={emailing}
        onClose={() => setEmailing(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        label="Delete meeting"
        title={`Delete "${meeting.title || "Untitled meeting"}"?`}
        error={remove.error && errorMessage(remove.error)}
        cancelLabel="Cancel"
        confirmLabel="Delete meeting"
        loading={remove.isLoading}
        onConfirm={() =>
          remove.mutate(meeting.sqid, {
            onSuccess: () => {
              setConfirmDelete(false);
              toast("Meeting deleted", { type: "success" });
              close();
            },
          })
        }
      >
        The transcript, summary and notes will be deleted for good. A copy of
        the recording on the device that made it isn't affected.
      </ConfirmDialog>
    </div>
  );
}

// Chip, title and a muted meta line, with actions on the right
export function DetailHeader({ chip, title, meta, actions }) {
  return (
    <div
      style={{
        display: "flex",
        gap: "12px",
        alignItems: "flex-start",
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: "1 1 280px", minWidth: 0 }}>
        {chip}
        {title}
        {meta && (
          <p style={{ ...ui.muted, margin: "4px 0 0", fontSize: "13px" }}>
            {meta}
          </p>
        )}
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {actions}
      </div>
    </div>
  );
}

export const detailTitle = (size) => ({
  margin: "8px 0 0",
  fontSize: `${size}px`,
  lineHeight: 1.25,
  color: colors.text,
  fontWeight: 700,
});

function EditableTitle({ value, size, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== value) onSave(next);
    else setDraft(value);
  };

  const heading = detailTitle(size);

  if (editing) {
    return (
      <TextInput
        value={draft}
        autoFocus
        maxLength={150}
        aria-label="Meeting title"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setDraft(value);
            setEditing(false);
          }
        }}
        style={{
          ...heading,
          padding: "2px 8px",
          borderColor: colors.teal,
        }}
      />
    );
  }

  return (
    <h1
      style={{ ...heading, display: "flex", alignItems: "center", gap: "8px" }}
    >
      <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {value || "Untitled meeting"}
      </span>
      <IconButton
        icon="pencil"
        label="Rename meeting"
        title="Rename"
        onClick={() => setEditing(true)}
        style={{ padding: "4px" }}
      />
    </h1>
  );
}

const STEPS = [
  {
    done: ["PENDING", "TRANSCRIBING", "SUMMARISING"],
    label: "Uploaded from the Sidekick app",
    current: ["PENDING_UPLOAD"],
    currentLabel: "Waiting for the recording to upload",
  },
  {
    done: ["SUMMARISING"],
    label: "Transcribed",
    current: ["PENDING", "TRANSCRIBING"],
    currentLabel: "Transcribing the recording",
  },
  {
    done: [],
    label: "Summary and notes",
    current: ["SUMMARISING"],
    currentLabel: "Writing the summary and notes",
  },
];

function ProcessingCard({ meeting }) {
  const status = meeting.status.value;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        padding: "16px",
        borderRadius: "12px",
        background: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      {STEPS.map((step, i) => {
        const done = step.done.includes(status);
        const current = step.current.includes(status);
        return (
          <div
            key={i}
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
              fontSize: "14px",
              opacity: done || current ? 1 : 0.55,
            }}
          >
            <span
              style={{
                flex: "none",
                width: "22px",
                height: "22px",
                boxSizing: "border-box",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 700,
                ...(done
                  ? { background: colors.green, color: colors.onPrimary }
                  : {
                      border: `2px solid ${current ? colors.amber : colors.textFaint}`,
                      color: current ? colors.amber : colors.textFaint,
                    }),
              }}
            >
              {done ? <Icon name="check" size={12} /> : i + 1}
            </span>
            <span>
              {current ? step.currentLabel : step.label}
              {current && (
                <span
                  style={{ ...ui.muted, display: "block", fontSize: "12px" }}
                >
                  {status === "PENDING_UPLOAD"
                    ? "The app uploads it once it's online. Keep it open on the device that recorded it."
                    : "Usually a minute or two. This page updates by itself."}
                </span>
              )}
            </span>
            {current && status !== "PENDING_UPLOAD" && (
              <span style={{ marginLeft: "auto" }}>
                <Spinner size={14} color={colors.amber} />
              </span>
            )}
          </div>
        );
      })}
      {status === "SUMMARISING" && meeting.has_transcription && (
        <p style={{ ...ui.muted, margin: 0, fontSize: "13px" }}>
          The transcript is ready to read while you wait.
        </p>
      )}
    </div>
  );
}

function FailedCard({ meeting, onDelete }) {
  return (
    <Banner
      tone="error"
      icon="alert"
      title={
        meeting.status.value === "DID_NOT_TRANSCRIBE"
          ? "Didn't transcribe"
          : "Couldn't process this meeting"
      }
      action={
        <div>
          <Btn icon="trash" onClick={onDelete}>
            Delete meeting
          </Btn>
        </div>
      }
    >
      {meeting.error_message ||
        "Sidekick couldn't transcribe this recording, so there's nothing to summarise."}{" "}
      <span style={ui.muted}>Delete it if it was recorded by mistake.</span>
    </Banner>
  );
}

function NotSummarisedCard({ meeting, loading, onSummarise, onDelete }) {
  return (
    <Banner
      tone="warning"
      icon="alert"
      title={
        meeting.summary
          ? "The latest summary didn't finish"
          : "Didn't summarise"
      }
      action={
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <Btn
            variant="primary"
            icon="refresh"
            loading={loading}
            onClick={onSummarise}
          >
            Summarise again
          </Btn>
          {!meeting.summary && (
            <Btn icon="trash" onClick={onDelete}>
              Delete meeting
            </Btn>
          )}
        </div>
      }
    >
      {meeting.error_message ||
        "There's no summary for this meeting yet. The transcript may be too short."}
      {meeting.summary && (
        <span style={ui.muted}>
          {" "}
          The previous summary is still shown below.
        </span>
      )}
    </Banner>
  );
}
